/**
 * OverrideReasonModal Component
 *
 * Modal for when a human reviewer disagrees with the AI determination
 * and needs to provide a reason for the override.
 */

import { useState } from 'react';
import { X, AlertTriangle } from 'lucide-react';
import type { ResultType } from '../../types';
import { RESULT_TYPE_LABELS } from '../../types';

interface OverrideReasonModalProps {
  isOpen: boolean;
  aiResult: ResultType;
  criterionText: string;
  onSubmit: (result: ResultType, reason: string) => Promise<void>;
  onClose: () => void;
}

const MIN_REASON_LENGTH = 20;

const RESULT_OPTIONS: Array<{ value: ResultType; label: string }> = [
  { value: 'met', label: 'Met' },
  { value: 'not_met', label: 'Not Met' },
  { value: 'uncertain', label: 'Uncertain' },
];

export function OverrideReasonModal({
  isOpen,
  aiResult,
  criterionText,
  onSubmit,
  onClose,
}: OverrideReasonModalProps) {
  const [selectedResult, setSelectedResult] = useState<ResultType | null>(null);
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    if (!selectedResult) {
      setError('Please select your determination');
      return;
    }

    if (reason.trim().length < MIN_REASON_LENGTH) {
      setError(`Please provide at least ${MIN_REASON_LENGTH} characters explaining your decision`);
      return;
    }

    // Only close after the write succeeds. If onSubmit rejects, keep the modal
    // open and surface the error — otherwise a failed override reads as recorded.
    setIsSubmitting(true);
    try {
      await onSubmit(selectedResult, reason.trim());
      handleClose();
    } catch {
      setError('Save failed — override NOT recorded. Please retry.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedResult(null);
    setReason('');
    setError('');
    onClose();
  };

  const isSubmitDisabled = !selectedResult || reason.trim().length < MIN_REASON_LENGTH;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
        onClick={isSubmitting ? undefined : handleClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-lg w-full overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 dark:bg-red-900/40 rounded-lg">
                <AlertTriangle size={20} className="text-red-600 dark:text-red-400" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Override AI Determination</h2>
            </div>
            <button
              onClick={handleClose}
              disabled={isSubmitting}
              className="p-1 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 rounded transition-colors disabled:opacity-50"
              aria-label="Close modal"
            >
              <X size={20} />
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-4 space-y-4">
            {/* AI result display */}
            <div className="p-3 bg-gray-50 dark:bg-gray-900/40 rounded-lg">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">AI determined:</p>
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {RESULT_TYPE_LABELS[aiResult]}
              </p>
            </div>

            {/* Criterion text */}
            <div>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Criterion</p>
              <p className="text-sm text-gray-900 dark:text-gray-100 p-3 bg-gray-50 dark:bg-gray-900/40 rounded-lg">
                {criterionText}
              </p>
            </div>

            {/* Human determination */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Your determination: <span className="text-red-500 dark:text-red-400">*</span>
              </label>
              <div className="flex gap-4">
                {RESULT_OPTIONS.map((option) => (
                  <label key={option.value} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="humanResult"
                      value={option.value}
                      checked={selectedResult === option.value}
                      onChange={() => {
                        setSelectedResult(option.value);
                        setError('');
                      }}
                      className="w-4 h-4 text-primary-600 dark:text-primary-400 border-gray-300 dark:border-gray-600 focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">{option.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Reason textarea */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Reason for override: <span className="text-red-500 dark:text-red-400">*</span>
              </label>
              <textarea
                value={reason}
                onChange={(e) => {
                  setReason(e.target.value);
                  setError('');
                }}
                rows={4}
                placeholder="Please explain why you disagree with the AI assessment..."
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Please explain why you disagree with the AI assessment (minimum {MIN_REASON_LENGTH} characters)
              </p>
              {reason.length > 0 && reason.length < MIN_REASON_LENGTH && (
                <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                  {MIN_REASON_LENGTH - reason.length} more characters required
                </p>
              )}
            </div>

            {/* Error message */}
            {error && (
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            )}

            {/* Audit warning */}
            <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
              <AlertTriangle size={16} className="text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-800 dark:text-amber-300">
                This action will be logged for audit purposes. Your override decision and reasoning
                will be recorded in the review audit trail.
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40">
            <button
              onClick={handleClose}
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors min-h-[44px] sm:min-h-0"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={isSubmitDisabled || isSubmitting}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors min-h-[44px] sm:min-h-0"
            >
              {isSubmitting ? 'Submitting…' : 'Submit Override'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
